/**
 * 세이브 저장소 백엔드.
 *
 * 데스크톱 앱에서는 OS 앱 데이터 폴더의 파일에 저장하고,
 * 브라우저에서 열었을 때는 localStorage 로 폴백한다.
 * (경로: macOS `~/Library/Application Support/dev.henesys.slice/save.json`,
 *  Windows `%APPDATA%\dev.henesys.slice\save.json`,
 *  Linux `~/.local/share/dev.henesys.slice/save.json`)
 */

import { isDesktop } from '../platform/runtime';

export interface SaveStorage {
  readonly kind: 'file' | 'localStorage' | 'memory';
  /** 사용자에게 보여줄 저장 위치 설명 */
  readonly location: string;
  read(): Promise<string | null>;
  write(text: string): Promise<void>;
  remove(): Promise<void>;
}

const FILE_NAME = 'save.json';
const LOCAL_KEY = 'maple-slice.save.v1';

/** Tauri fs 플러그인을 쓰는 파일 저장소 */
async function createFileStorage(): Promise<SaveStorage> {
  const fs = await import('@tauri-apps/plugin-fs');
  const { appDataDir } = await import('@tauri-apps/api/path');
  const dir = await appDataDir();
  const base = { baseDir: fs.BaseDirectory.AppData } as const;

  return {
    kind: 'file',
    location: `${dir}${FILE_NAME}`,
    async read() {
      if (!(await fs.exists(FILE_NAME, base))) return null;
      return fs.readTextFile(FILE_NAME, base);
    },
    async write(text) {
      if (!(await fs.exists('', base))) {
        await fs.mkdir('', { ...base, recursive: true });
      }
      await fs.writeTextFile(FILE_NAME, text, base);
    },
    async remove() {
      if (!(await fs.exists(FILE_NAME, base))) return;
      await fs.writeTextFile(FILE_NAME, '', base);
    },
  };
}

function createLocalStorage(): SaveStorage {
  return {
    kind: 'localStorage',
    location: `localStorage['${LOCAL_KEY}']`,
    read: async () => {
      try {
        return localStorage.getItem(LOCAL_KEY);
      } catch {
        return null;
      }
    },
    write: async (text) => {
      try {
        localStorage.setItem(LOCAL_KEY, text);
      } catch {
        /* 프라이빗 모드 등에서 실패해도 게임 진행은 막지 않는다 */
      }
    },
    remove: async () => {
      try {
        localStorage.removeItem(LOCAL_KEY);
      } catch {
        /* 무시 */
      }
    },
  };
}

function createMemoryStorage(): SaveStorage {
  let value: string | null = null;
  return {
    kind: 'memory',
    location: '메모리 (저장되지 않음)',
    read: async () => value,
    write: async (text) => {
      value = text;
    },
    remove: async () => {
      value = null;
    },
  };
}

/** 환경에 맞는 저장소를 고른다. 파일 저장소 준비가 실패하면 폴백한다. */
export async function resolveStorage(): Promise<SaveStorage> {
  if (isDesktop()) {
    try {
      return await createFileStorage();
    } catch (error) {
      console.warn('[save] 파일 저장소를 열 수 없어 localStorage 로 폴백합니다', error);
    }
  }
  try {
    if (typeof localStorage !== 'undefined') return createLocalStorage();
  } catch {
    /* 아래에서 메모리 저장소로 폴백 */
  }
  return createMemoryStorage();
}
